import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useFamilyStore } from '@/stores'
import { familyApi } from '@/lib/api'
import type {
  CreateFamilyRequest,
  InviteMemberRequest,
  UpdateMemberRequest,
  Family,
} from '@/types'

const QUERY_KEYS = {
  families: ['families'] as const,
  family: (id: string) => ['families', id] as const,
  members: (familyId: string) => ['families', familyId, 'members'] as const,
  invitations: (familyId: string) => ['families', familyId, 'invitations'] as const,
  pendingInvitations: ['invitations', 'pending'] as const,
}

export function useFamily() {
  const queryClient = useQueryClient()
  const {
    families,
    selectedFamilyId,
    setFamilies,
    setSelectedFamilyId,
    getSelectedFamily,
  } = useFamilyStore()

  // Fetch all families for current user
  const familiesQuery = useQuery({
    queryKey: QUERY_KEYS.families,
    queryFn: familyApi.list,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  // Sync families to store when data changes
  useEffect(() => {
    if (familiesQuery.data) {
      setFamilies(familiesQuery.data)
    }
  }, [familiesQuery.data, setFamilies])

  // Get selected family details
  const selectedFamily = getSelectedFamily()

  // Create family mutation
  const createFamilyMutation = useMutation({
    mutationFn: (data: CreateFamilyRequest) => familyApi.create(data),
    onSuccess: (newFamilyDetail) => {
      // Convert FamilyDetail to Family (creator is always PRIMARY_CAREGIVER)
      const newFamily: Family = {
        id: newFamilyDetail.id,
        name: newFamilyDetail.name,
        role: 'PRIMARY_CAREGIVER' as const,
        member_count: newFamilyDetail.member_count,
        created_at: newFamilyDetail.created_at,
      }

      // Update BOTH query cache AND store to prevent race condition
      // Setting query data directly ensures the useEffect sync won't overwrite our update
      queryClient.setQueryData<Family[]>(QUERY_KEYS.families, (old) => {
        const current = Array.isArray(old) ? old : []
        return [...current, newFamily]
      })

      // Also update the store for immediate access
      const currentFamilies = useFamilyStore.getState().families
      setFamilies([...currentFamilies, newFamily])

      // NOTE: Do NOT call invalidateQueries here - it causes a race condition where
      // the refetch can complete while the wizard is still sending invitations,
      // overwriting our optimistic update with stale server data
    },
  })

  // Update family mutation
  const updateFamilyMutation = useMutation({
    mutationFn: ({ familyId, data }: { familyId: string; data: Partial<CreateFamilyRequest> }) =>
      familyApi.update(familyId, data),
    onSuccess: (_, { familyId }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.families })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.family(familyId) })
    },
  })

  // Delete family mutation
  const deleteFamilyMutation = useMutation({
    mutationFn: (familyId: string) => familyApi.delete(familyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.families })
    },
  })

  // User has families if either the store or the query data has them
  // This prevents race conditions where one source is updated but not the other
  const hasFamilies = families.length > 0 || (familiesQuery.data?.length ?? 0) > 0

  // Only true when query has successfully completed AND returned empty
  // This is the ONLY time we should redirect to onboarding
  const confirmedNoFamilies = familiesQuery.isSuccess && !hasFamilies

  return {
    // State
    families,
    selectedFamily,
    selectedFamilyId,
    hasFamilies,
    confirmedNoFamilies,
    isLoading: familiesQuery.isLoading,
    error: familiesQuery.error,

    // Actions
    setSelectedFamilyId,
    refetch: familiesQuery.refetch,

    // Mutations
    createFamily: createFamilyMutation.mutateAsync,
    updateFamily: updateFamilyMutation.mutateAsync,
    deleteFamily: deleteFamilyMutation.mutateAsync,
    isCreating: createFamilyMutation.isPending,
    isUpdating: updateFamilyMutation.isPending,
    isDeleting: deleteFamilyMutation.isPending,
  }
}

export function useFamilyDetail(familyId: string | null) {
  return useQuery({
    queryKey: QUERY_KEYS.family(familyId!),
    queryFn: () => familyApi.get(familyId!),
    enabled: !!familyId,
    staleTime: 5 * 60 * 1000,
  })
}

export function useFamilyMembers(familyId: string | null) {
  const queryClient = useQueryClient()
  const {
    selectedMemberId,
    setSelectedMemberId,
    setMembers,
    getSelectedMember,
    getParentMembers,
  } = useFamilyStore()

  const membersQuery = useQuery({
    queryKey: QUERY_KEYS.members(familyId!),
    queryFn: () => familyApi.getMembers(familyId!),
    enabled: !!familyId,
    staleTime: 5 * 60 * 1000,
  })

  // Sync members to store when data changes
  useEffect(() => {
    if (membersQuery.data) {
      setMembers(membersQuery.data)
    }
  }, [membersQuery.data, setMembers])

  const updateMemberMutation = useMutation({
    mutationFn: ({
      memberId,
      data,
    }: {
      memberId: string
      data: UpdateMemberRequest
    }) => familyApi.updateMember(familyId!, memberId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.members(familyId!) })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.families })
    },
  })

  const removeMemberMutation = useMutation({
    mutationFn: (memberId: string) => familyApi.removeMember(familyId!, memberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.members(familyId!) })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.families })
    },
  })

  const leaveFamilyMutation = useMutation({
    mutationFn: () => familyApi.leaveFamily(familyId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.families })
    },
  })

  return {
    members: membersQuery.data ?? [],
    isLoading: membersQuery.isLoading,
    error: membersQuery.error,
    refetch: membersQuery.refetch,

    // Member selection (for caregivers viewing parents)
    selectedMemberId,
    setSelectedMemberId,
    selectedMember: getSelectedMember(),
    parentMembers: getParentMembers(),

    updateMember: updateMemberMutation.mutateAsync,
    removeMember: removeMemberMutation.mutateAsync,
    leaveFamily: leaveFamilyMutation.mutateAsync,
    isUpdating: updateMemberMutation.isPending,
    isRemoving: removeMemberMutation.isPending,
    isLeaving: leaveFamilyMutation.isPending,
  }
}

export function useFamilyInvitations(familyId: string | null) {
  const queryClient = useQueryClient()

  const invitationsQuery = useQuery({
    queryKey: QUERY_KEYS.invitations(familyId!),
    queryFn: () => familyApi.getInvitations(familyId!),
    enabled: !!familyId,
    staleTime: 5 * 60 * 1000,
  })

  const inviteMemberMutation = useMutation({
    mutationFn: (data: InviteMemberRequest) => familyApi.inviteMember(familyId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invitations(familyId!) })
    },
  })

  const cancelInvitationMutation = useMutation({
    mutationFn: (invitationId: string) => familyApi.cancelInvitation(familyId!, invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invitations(familyId!) })
    },
  })

  const resendInvitationMutation = useMutation({
    mutationFn: (invitationId: string) => familyApi.resendInvitation(familyId!, invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invitations(familyId!) })
    },
  })

  return {
    invitations: invitationsQuery.data ?? [],
    isLoading: invitationsQuery.isLoading,
    error: invitationsQuery.error,
    refetch: invitationsQuery.refetch,

    inviteMember: inviteMemberMutation.mutateAsync,
    cancelInvitation: cancelInvitationMutation.mutateAsync,
    resendInvitation: resendInvitationMutation.mutateAsync,
    isInviting: inviteMemberMutation.isPending,
    isCancelling: cancelInvitationMutation.isPending,
    isResending: resendInvitationMutation.isPending,
  }
}

export function usePendingInvitations() {
  const queryClient = useQueryClient()

  const pendingQuery = useQuery({
    queryKey: QUERY_KEYS.pendingInvitations,
    queryFn: familyApi.getPendingInvitations,
    staleTime: 60 * 1000, // 1 minute
  })

  const acceptInvitationMutation = useMutation({
    mutationFn: (invitationId: string) => familyApi.acceptInvitation(invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.pendingInvitations })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.families })
    },
  })

  const declineInvitationMutation = useMutation({
    mutationFn: (invitationId: string) => familyApi.declineInvitation(invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.pendingInvitations })
    },
  })

  return {
    invitations: pendingQuery.data ?? [],
    isLoading: pendingQuery.isLoading,
    error: pendingQuery.error,
    refetch: pendingQuery.refetch,

    acceptInvitation: acceptInvitationMutation.mutateAsync,
    declineInvitation: declineInvitationMutation.mutateAsync,
    isAccepting: acceptInvitationMutation.isPending,
    isDeclining: declineInvitationMutation.isPending,
  }
}
